import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Shield, Save, Lock } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const PermissionsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentRole] = useState(localStorage.getItem('role'));
  const [permissions, setPermissions] = useState([]);

  const roleLabels = {
    managing_director: 'Managing Director',
    administration_manager: 'Administration Manager',
    service_coordinator: 'Service Coordinator',
    account_manager: 'Account Manager'
  };

  const roleColors = {
    managing_director: 'border-red-500/30 bg-red-500/10',
    administration_manager: 'border-orange-500/30 bg-orange-500/10',
    service_coordinator: 'border-blue-500/30 bg-blue-500/10',
    account_manager: 'border-green-500/30 bg-green-500/10'
  };

  const permissionGroups = {
    'General': {
      view_dashboard: 'View Dashboard',
      view_contacts: 'View Contact Submissions'
    },
    'Clients': {
      manage_clients: 'Manage Clients'
    },
    'Quotes': {
      create_quotes: 'Create Quotes',
      edit_quotes: 'Edit Quotes',
      delete_quotes: 'Delete Quotes',
      convert_quotes: 'Convert Quotes to Invoices'
    },
    'Invoices': {
      create_invoices: 'Create Invoices',
      edit_invoices: 'Edit Invoices',
      delete_invoices: 'Delete Invoices'
    },
    'Suppliers': {
      manage_suppliers: 'Manage Suppliers',
      create_suppliers: 'Create Suppliers',
      edit_suppliers: 'Edit Suppliers',
      delete_suppliers: 'Delete Suppliers'
    },
    'Products': {
      manage_products: 'Manage Products',
      create_products: 'Create Products',
      edit_products: 'Edit Products',
      delete_products: 'Delete Products'
    },
    'Purchase Orders': {
      manage_purchase_orders: 'Manage Purchase Orders',
      create_purchase_orders: 'Create Purchase Orders',
      edit_purchase_orders: 'Edit Purchase Orders',
      delete_purchase_orders: 'Delete Purchase Orders',
      approve_purchase_orders: 'Approve Purchase Orders'
    },
    'System Administration': {
      manage_users: 'Manage Admin Users',
      manage_settings: 'Manage System Settings',
      manage_permissions: 'Manage Role Permissions'
    }
  };

  useEffect(() => {
    if (currentRole === 'managing_director') {
      fetchPermissions();
    } else {
      setIsLoading(false);
    }
  }, [currentRole]);

  const fetchPermissions = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/permissions/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data);
    } catch (error) {
      toast.error('Failed to load permissions');
      console.error('Error fetching permissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePermission = (roleIndex, permissionKey, value) => {
    const newPermissions = [...permissions];
    newPermissions[roleIndex].permissions[permissionKey] = value;
    setPermissions(newPermissions);
  };

  const savePermissions = async (role, rolePermissions) => {
    setIsSaving(true);
    const token = localStorage.getItem('token');

    try {
      await axios.put(
        `${API}/permissions/role/${role}`,
        rolePermissions,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`Permissions updated for ${roleLabels[role]}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
      console.error('Error updating permissions:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (currentRole !== 'managing_director') {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-8">
            <CardContent className="text-center">
              <Lock className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Restricted Access</h2>
              <p className="text-gray-400">Only Managing Director can manage role permissions.</p>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="permissions-page" className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Role Permissions</h1>
          <p className="text-gray-400">Configure what each role can access and do in the system</p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-400">Loading permissions...</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {permissions.map((rolePerms, roleIndex) => (
              <Card 
                key={rolePerms.role} 
                className={`bg-white/5 backdrop-blur-sm border ${roleColors[rolePerms.role] || 'border-white/10'}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="w-6 h-6 text-orange-500" />
                      <CardTitle className="text-white text-xl">
                        {roleLabels[rolePerms.role] || rolePerms.role}
                      </CardTitle>
                    </div>
                    <Button
                      onClick={() => savePermissions(rolePerms.role, rolePerms.permissions)}
                      disabled={isSaving}
                      size="sm"
                      className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white border-0"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {Object.entries(permissionGroups).map(([groupName, groupPerms]) => (
                      <div key={groupName}>
                        <h3 className="text-orange-400 font-semibold mb-3 text-sm uppercase tracking-wide">
                          {groupName}
                        </h3>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {Object.entries(groupPerms).map(([key, label]) => (
                            <div 
                              key={key} 
                              className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                            >
                              <Label 
                                htmlFor={`${rolePerms.role}-${key}`}
                                className="text-gray-300 text-sm cursor-pointer"
                              >
                                {label}
                              </Label>
                              <Switch
                                id={`${rolePerms.role}-${key}`}
                                checked={rolePerms.permissions[key] || false}
                                onCheckedChange={(checked) => updatePermission(roleIndex, key, checked)}
                                className="data-[state=checked]:bg-orange-500"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Warning for Managing Director */}
                  {rolePerms.role === 'managing_director' && (
                    <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-yellow-400 text-sm">
                        <strong>Note:</strong> Managing Director should maintain full access to ensure system administration.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PermissionsPage;
